import { Grid, makeStyles } from '@material-ui/core'
import clsx from 'clsx'
import Accordion from 'components/Accordion'
import Button from 'components/Button'
import { useConfirmationDialog } from 'components/Dialog/ConfirmDialog'
import { FieldText } from 'components/Fields'
import Filters from 'components/FilterList/Filters'
import PropTypes from 'prop-types'
import React, { Fragment, forwardRef, useEffect, useImperativeHandle, useState } from 'react'
import { Form } from 'react-final-form'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { getTipoUsuarioLogado } from 'store/modules/auth/auth.selectors'
import { callAtualizarRegioesHidrograficas, callCriarRegioesHidrograficas, callExcluirRegioesHidrograficas } from 'store/modules/regioesHidrograficas'
import { getIsSavingRegioesHidrograficas, getIsUpdatingRegioesHidrograficas, getRegioesHidrograficas } from 'store/modules/regioesHidrograficas/regioesHidrograficas.selectors'
import { TIPO_USUARIO } from 'utils/constantes'
import { validateFormValues } from 'utils/forms'
import * as yup from 'yup'

const useStyles = makeStyles((theme) => ({
  rowText: {
    display: 'flex',
    alignItems: 'center',
    height: theme.spacing(5),
    fontSize: theme.typography.pxToRem(14),
  },
  search: {
    display: 'flex',
    justifyContent: 'space-between',
    width: (props) => props.fullWidth && '100%',
  },
  hidden: {
    display: 'none',
  },
  formAdd: {
    transition: '0.20s ease-out',
    maxHeight: 0,
    overflow: 'hidden',
  },
  openFormAdd: {
    marginTop: '-10px',
    paddingTop: 10,
    maxHeight: '1300px',
    transition: '0.25s ease-in',
  },
  normalTop: {
    marginTop: '16px',
  },
  alignRight: {
    display: 'flex',
    justifyContent: 'flex-end',
    paddingRight: '0 !important',
  },
  itemAlignRight: {
    maxWidth: '190px',
  },
  listItems: {
    marginTop: '32px',
    border: '1px solid #C9CEDB',
    boxShadow: '0px 8px 32px rgba(15, 52, 68, 0.08)',
    borderRadius: '8px',
    padding: '0px 16px',
  },
  itemTitle: {
    fontFamily: 'Poppins',
    color: '#0F3444',
    fontWeight: '600',
    fontSize: '16px',
  },
  divider: {
    height: 1,
    width: 'calc(100% + 32px)',
    marginLeft: '-16px',
    backgroundColor: theme.palette.grey.main,
  },
  dividerField: {
    borderBottom: `1px solid ${theme.palette.grey.main}`,
    margin: '8px 0',
  },
  itemsForm: {
    display: 'flex',
    flexWrap: 'wrap',
    columnGap: '8px',
    rowGap: '20px',
  },
  itemFormField: {
    width: 'calc( 49% - 1px )',
  },
}))

const TabRegioes = forwardRef(({ paisId, controlAdded, setControlAdded }, ref) => {
  const { t } = useTranslation()
  const classes = useStyles()
  const dispatch = useDispatch()

  const isSaving = useSelector(getIsSavingRegioesHidrograficas)
  const isUpdating = useSelector(getIsUpdatingRegioesHidrograficas)
  const tipoUsuarioLogado = useSelector(getTipoUsuarioLogado)

  const desabilitarFormulario = Boolean(tipoUsuarioLogado === TIPO_USUARIO.ADMIN.OBSERVADOR)

  const filterScreen = [
    { value: true, label: 'filtros.nomeAZ', name: 'order' },
    { value: false, label: 'filtros.nomeZA', name: 'order' },
  ]
  const dialog = useConfirmationDialog()
  const listaRegioesHidrograficas = useSelector(getRegioesHidrograficas)
  const [data, setData] = useState(listaRegioesHidrograficas)

  const validation = yup.object({
    nome: yup.string().required(),
    balancoDemandaDisponibilidade: yup.number().required(),
    valorSeveridade: yup.number().required(),
    link: yup.string().url(),
  })

  const handleUpdate = (values) => {
    const { id } = values
    const payload = ({ ...values, paisId })
    delete payload.id
    dispatch(callAtualizarRegioesHidrograficas({ id, regioesHidrograficas: payload }))
  }

  const handleSave = (values, form) => {
    const payload = ({ ...values, paisId })
    dispatch(callCriarRegioesHidrograficas(payload))
    form.reset()
    Object.keys(values).forEach((key) => {
      form.resetFieldState(key)
    })
  }

  const handleDelete = (id, regioesHidrograficas) => dialog.showConfirmation({
    title: t('dialogs.confirmarExclusao'),
    message: t('dialogs.excluirRegioesHidrograficas', {
      regioesHidrograficas,
    }),
  }).then((result) => {
    if (result) {
      dispatch(callExcluirRegioesHidrograficas({ id }))
    }
    return null
  })

  const [openItem, setOpenItem] = useState('')

  const handleExpand = (item) => {
    setOpenItem(openItem === item ? '' : item)
  }

  useImperativeHandle(
    ref,
    () => ({
      handleButtonAdded() {
        setControlAdded(true)
        handleExpand('')
      },
    }),
  )

  const clearFields = (form, values, type) => {
    if (type === 'add') {
      setControlAdded(!controlAdded)
    }
    form.reset()
    Object.keys(values).forEach((key) => {
      form.resetFieldState(key)
    })
  }

  const formFields = [
    {
      name: 'nome',
      label: t('inputs.regiaoHidrografica'),
      type: 'text',
      required: true,
    },
    {
      name: 'link',
      label: t('inputs.LinkParaMapa'),
      type: 'text',
      required: false,
    },
    {
      name: 'disponibilidade',
      label: t('inputs.Disponibilidadem3s'),
      type: 'number',
      required: false,
    },
    {
      name: 'demanda',
      label: t('inputs.Demandam3s'),
      type: 'number',
      required: false,
    },
    {
      name: 'balancoDemandaDisponibilidade',
      label: t('inputs.BalancoBDD'),
      type: 'number',
      required: true,
    },
    {
      name: 'valorSeveridade',
      label: t('inputs.Severidade'),
      type: 'number',
      required: true,
    },
  ]

  const FieldsForm = () => (
    <div className={classes.itemsForm}>
      {formFields.map((elem, index) => (
        <FieldText
          required={elem.required}
          label={elem.label}
          name={elem.name}
          key={index}
          className={classes.itemFormField}
          type={elem.type}
          disabled={desabilitarFormulario}
        />
      ))}
    </div>
  )

  const ItemForm = (position) => {
    const itemRegiaoHidrografica = [data[position]]
    return (
      <Form
        onSubmit={handleUpdate}
        validate={validateFormValues(validation)}
        initialValues={{
          nome: itemRegiaoHidrografica[0]?.nome ?? '',
          balancoDemandaDisponibilidade: itemRegiaoHidrografica[0]?.balancoDemandaDisponibilidade ?? '',
          valorSeveridade: itemRegiaoHidrografica[0]?.valorSeveridade ?? '',
          link: itemRegiaoHidrografica[0]?.link ?? '',
          disponibilidade: itemRegiaoHidrografica[0]?.disponibilidade ?? null,
          demanda: itemRegiaoHidrografica[0]?.demanda ?? null,
          id: itemRegiaoHidrografica[0]?.id ?? '',
        }}
        render={({ handleSubmit, form, pristine, valid }) => (
          <form onSubmit={handleSubmit}>
            <Accordion
              hideButton
              variant="no-borders"
              classes={{ title: classes.itemTitle }}
              title={itemRegiaoHidrografica[0]?.nome}
              onDelete={() => {
                handleDelete(
                  itemRegiaoHidrografica[0]?.id,
                  itemRegiaoHidrografica[0]?.tipo,
                )
                handleExpand('')
              }}
              expanded={position === openItem}
              onExpand={() => handleExpand(position)}
              disableDelete={desabilitarFormulario}
            >
              <Grid container>
                { FieldsForm() }
                <Grid item xs={12}>
                  <Grid
                    container
                    direction="row"
                    spacing={2}
                    justifyContent="space-between"
                    className={classes.normalTop}
                  >
                    <Grid item className={classes.deleteButton}>
                      <Button
                        variant="danger"
                        onClick={() => { form.reset(); handleExpand('') }}
                      >
                        {t('inputs.cancelar')}
                      </Button>
                    </Grid>
                    <Grid item>
                      <Button
                        type="submit"
                        disabled={Boolean(pristine || !valid) || desabilitarFormulario}
                        loading={isUpdating}
                      >
                        {t('inputs.salvar')}
                      </Button>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </Accordion>
            <div className={classes.divider} />
          </form>
        )}
      />
    )
  }

  useEffect(() => {
    setData(listaRegioesHidrograficas)
  }, [listaRegioesHidrograficas])

  return (
    <>
      <div className={clsx(classes.search, controlAdded && classes.hidden)}>
        <Filters
          fullWidth
          items={listaRegioesHidrograficas}
          setItems={setData}
          origin={{ vertical: 'top', horizontal: 'right' }}
          filterScreen={filterScreen}
        />
      </div>
      {/* <Button
        variant="default"
        disabled={controlAdded}
        onClick={() => {
          setControlAdded(true)
          handleExpand('')
        }}
      >
        {t('inputs.AdicionarRegiao')}
      </Button> */}
      <div
        className={clsx(
          classes.formAdd,
          controlAdded && classes.openFormAdd,
        )}
      >
        <Form
          onSubmit={handleSave}
          initialValues={{
            nome: '',
            balancoDemandaDisponibilidade: '',
            valorSeveridade: '',
            link: '',
            disponibilidade: null,
            demanda: null,
          }}
          validate={validateFormValues(validation)}
          render={({ handleSubmit, form, values, valid }) => (
            <form onSubmit={handleSubmit}>
              <Grid container>
                { FieldsForm() }
                <Grid item xs={12}>
                  <Grid
                    container
                    direction="row"
                    spacing={2}
                    justifyContent="space-between"
                    className={classes.normalTop}
                  >
                    <Grid item className={classes.deleteButton}>
                      <Button
                        variant="danger"
                        onClick={(e) => {
                          e.preventDefault()
                          clearFields(form, values, 'add')
                        }}
                      >
                        {t('inputs.cancelar')}
                      </Button>
                    </Grid>
                    <Grid item>
                      <Button
                        type="submit"
                        disabled={!valid}
                        loading={isSaving}
                      >
                        {t('inputs.salvar')}
                      </Button>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </form>
          )}
        />
      </div>
      <div className={classes.listItems}>
        {data.length > 0 && data.map((elem, index) => (
          <Fragment key={index}>
            {ItemForm(index)}
          </Fragment>
        ))}
      </div>
    </>
  )
})

TabRegioes.propTypes = {
  paisId: PropTypes.number,
  controlAdded: PropTypes.bool,
  setControlAdded: PropTypes.func,
}

export default TabRegioes
